import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetIapUserQuery } from '@/services/customizationApi';
import { cdnImage } from '@/lib/cdnImage';
import { cn } from '@/lib/cn';
import { UserBgShape } from './UserBgShape';

interface UserHeaderBannerProps {
  /** QLASH user id — used to resolve the IAP user record + selected banner. */
  userId: number | string | undefined | null;
  /**
   * Optional avatar URL / fallback character. Rendered as a circular thumb
   * on the left side of the banner, ringed in the brand colour to echo the
   * mobile-app's `UserBanner` design.
   */
  avatarUrl?: string | null;
  /**
   * How to fit the avatar inside the 56px circle.
   * - `cover` (default) crops to fill — right for Telegram-style headshot pics.
   * - `contain` shows the whole image — right for transparent full-body QAvatars
   *   so the character isn't centre-cropped to its chest.
   */
  avatarFit?: 'cover' | 'contain';
  /**
   * When true, the avatar circle is not rendered at all. Use this when the
   * caller paints its own floating avatar on top of the banner (e.g. Hub's
   * featured QAvatar overlay).
   */
  hideAvatar?: boolean;
  avatarFallback?: string;
  /** Optional content rendered overlaying the banner (e.g. nickname strip). */
  children?: ReactNode;
  /**
   * When provided, the whole banner becomes a button that fires this on tap
   * (used by Hub / Profile to open the CustomizeSheet). No pencil overlay —
   * the banner itself is the affordance.
   */
  onCustomize?: () => void;
  className?: string;
}

/**
 * Esports-style profile header — chevron-clipped SVG, cyan stroke, banner
 * image (or brand gradient fallback) masked inside, circular avatar overlay
 * on the left and an edit pencil overlay on the right.
 *
 * Mirrors `mobile-app/src/components/hub/hub-header.tsx` lines 200–290.
 */
export function UserHeaderBanner({
  userId,
  avatarUrl,
  avatarFit = 'cover',
  hideAvatar = false,
  avatarFallback,
  children,
  onCustomize,
  className,
}: UserHeaderBannerProps) {
  const { t } = useTranslation('general');
  const { data: iapUser } = useGetIapUserQuery(userId ?? '', {
    skip: userId == null,
  });

  const banner = iapUser?.banner;
  const bgUrl = banner?.media_uri ? cdnImage(banner.media_uri) : null;

  const interactive = !!onCustomize;
  const ariaLabel = interactive
    ? t('banner.customize_aria', { defaultValue: 'Customize banner' })
    : undefined;

  const inner = (
    <>
      <UserBgShape imageUrl={bgUrl} className="absolute inset-0 h-full w-full" />
      <div className="absolute inset-0 flex items-center">
        {/* Avatar — mirrors mobile-app's hub-header.tsx Avatar_container
            (60px on a 395px-wide banner, offset ~10% from the left edge so
            it sits centred inside the cyan stroke without crowding it). */}
        <div className="flex items-center gap-3 pl-[10%]">
          {!hideAvatar && (
            <span
              className={cn(
                'flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-full',
                'bg-bg-tertiary ring-2 ring-brand',
              )}
            >
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt=""
                  className={cn(
                    'h-full w-full',
                    avatarFit === 'contain' ? 'object-contain' : 'object-cover',
                  )}
                  onError={(e) => {
                    // Hide broken images so the fallback initial shows through.
                    (e.currentTarget as HTMLImageElement).style.display = 'none';
                  }}
                />
              ) : (
                <span aria-hidden className="text-lg font-bold text-fg">
                  {(avatarFallback ?? '?').toUpperCase()}
                </span>
              )}
            </span>
          )}
          {children}
        </div>
      </div>
    </>
  );

  const containerClass = cn(
    'relative w-full',
    interactive && 'transition-opacity active:opacity-80',
    className,
  );
  const style = { aspectRatio: '395 / 99' } as const;

  if (interactive) {
    return (
      <button
        type="button"
        onClick={onCustomize}
        aria-label={ariaLabel}
        className={cn(containerClass, 'text-left')}
        style={style}
      >
        {inner}
      </button>
    );
  }
  return (
    <div className={containerClass} style={style}>
      {inner}
    </div>
  );
}
